import { AggregateBase } from "csi/action/AggregateBase";
import comm from "csi/comm";
import Request from "csi/def/Request";
import locale from "csi/locale";
import csi from "csi/main";
import xml from "csi/xml";
import declare from "dojo/_base/declare";
import lang from  "dojo/_base/lang";
import { C8Node } from "src/c8-platform/type/node";
import NewBOMLineItem from "../NewBOMLineItem";

const MODULE = "FormulatedProduct";
const NEW_FOOD_BOM_LINE_ITEM = "FoodBOMNewBomLineItem";
const NEW_FOOD_BOM_LINE_ITEM_FROM_MATERIAL = "FoodBOMNewBOMLineItemFromMaterial";
const NEW_FOOD_BOM_LINE_ITEM_FROM_SKU_MATERIAL = "FoodBOMNewBOMLineItemFromSKUMaterial";
const NEW_FOOD_BOM_LINE_ITEM_FROM_FOOD = "FoodBOMNewBOMLineItemFromFood";
const NEW_FOOD_BOM_LINE_ITEM_FROM_FORMULATED_MATERIAL = "FoodBOMNewBOMLineItemFromFormulatedMaterial";
const NEW_FOOD_BOM_LINE_ITEM_FROM_FOOD_SKU = "FoodBOMNewBOMLineItemFromFoodSKU";
const PRODUCT_TYPE_FIELD = locale.resource("ProductType");
const AGGREGATE_MATERIAL_PAGE = 1;
const AGGREGATE_SKU_MATERIAL_PAGE = 2;
const AGGREGATE_FORMULATED_MATERIAL_PAGE = 3;
const AGGREGATE_FOOD_PAGE = 4;
const AGGREGATE_FOOD_SKU_PAGE = 5;

const NewFoodBOMLineItemWizard = declare([NewBOMLineItem], {

	/** @property {QueryResult} bomSectionQueryResult - The QueryResult object if action is performed in a BOM Section*/
	bomSectionQueryResult: null,

	/**
	 * @override
	 * Use return value as parameter to success message based on action type
	 */
	getStringParameters() {
		const fd = this.m_formData;
		const nodes = fd.getSelectedNodes();
		return (nodes?.length) ? [`${nodes.length} ${locale.typePlural("FoodBOMLineItem")}`] : [locale.type("FoodBOMLineItem")];
	},

	/**
	 * @override
	 * Pass form data to the next page
	 */
	onNextPage(wizard, currentPage, nextPage, bFinish) {
		const index = wizard.getIndexOfPage(nextPage);
		const fd = this.m_formData;
		const cache = fd.getCache();
		if (index === 0) {
			return bFinish ? null : wizard.setFormDataToPage(nextPage, this.m_formData, (index >= 1));
		}
		const itemProductTypeEnum = fd.getFieldValue(PRODUCT_TYPE_FIELD);
		const foodBOMRevision = fd.getParent();
		const foodBOMSubtypeSettings: C8Node = cache.getPathNode(
			foodBOMRevision, 
			"Child:__Parent__/Child:Subtype/Child:Settings",
		);
		const itemActualPreference = foodBOMSubtypeSettings?.ActualItemsSelectionPreference[itemProductTypeEnum];
		
		const productType = xml.enumGetText(itemProductTypeEnum);
		const actualPreference = xml.enumGetText(itemActualPreference);
		if (index == AGGREGATE_MATERIAL_PAGE && (productType != "Material" || actualPreference != "BaseProduct")) {
			return wizard.skipToNextPage(nextPage);
		} else if (index == AGGREGATE_SKU_MATERIAL_PAGE && productType != "Material") {
			return wizard.skipToNextPage(nextPage);
		} else if (index == AGGREGATE_FORMULATED_MATERIAL_PAGE && productType != "FormulatedMaterial") {
			return wizard.skipToNextPage(nextPage);
		} else if (index == AGGREGATE_FOOD_PAGE && (productType != "Food" || actualPreference != "BaseProduct")) {
			return wizard.skipToNextPage(nextPage);
		} else if (index == AGGREGATE_FOOD_SKU_PAGE && productType != "Food") {
			return wizard.skipToNextPage(nextPage);
		}

		return bFinish ? null : wizard.setFormDataToPage(nextPage, this.m_formData, (index >= 1));
	},

	/**
	 * @override
	 *
	 * Conditionally create request to send to Server Operations,
	 * depending on selections made on FoodBOMItem wizard
	 */
	toWriter() {
		let request;
		let action;

		const fd = this.m_formData;
		const cache = fd.getCache();
		const selectedProducts = fd.getSelectedNodes();
		this.m_urlNode = null; // reset in the case that "Save and New" action on wizard is selected
		const itemProductTypeEnum = fd.getFieldValue(PRODUCT_TYPE_FIELD);
		const foodBOMRevision = fd.getParent();
		const foodBOMSubtypeSettings: C8Node = cache.getPathNode(
			foodBOMRevision, 
			"Child:__Parent__/Child:Subtype/Child:Settings",
		);
		const itemActualPreference = foodBOMSubtypeSettings?.ActualItemsSelectionPreference[itemProductTypeEnum];
		
		if (selectedProducts.length) {
			action = this.getAction() as AggregateBase;
			// Product selection was made on second page of wizard, aggregate action

			// TODO: m_operation could be dynamically different by a passed in parameter
			const productType = xml.enumGetText(itemProductTypeEnum);
			const actualPreference = xml.enumGetText(itemActualPreference);
			if (productType === "Material" && actualPreference === "BaseProduct") {
				request = lang.mixin(new Request(), {
					m_module: MODULE,
					m_operation: NEW_FOOD_BOM_LINE_ITEM_FROM_MATERIAL,
				});
			} else if (productType === "Food" && actualPreference === "BaseProduct") {
				request = lang.mixin(new Request(), {
					m_module: MODULE,
					m_operation: NEW_FOOD_BOM_LINE_ITEM_FROM_FOOD,
				});
			} else if (productType === "FormulatedMaterial") {
				request = lang.mixin(new Request(), {
					m_module: MODULE,
					m_operation: NEW_FOOD_BOM_LINE_ITEM_FROM_FORMULATED_MATERIAL,
				});
			} else if (productType === "Food") {
				request = lang.mixin(new Request(), {
					m_module: MODULE,
					m_operation: NEW_FOOD_BOM_LINE_ITEM_FROM_FOOD_SKU,
				});
			} else if (productType === "Material") {
				request = lang.mixin(new Request(), {
					m_module: MODULE,
					m_operation: NEW_FOOD_BOM_LINE_ITEM_FROM_SKU_MATERIAL,
				});
			}
			
			// C8FB-550: Set so that action can be processed as aggregate
			action.aggregateId = "Items";
			action.aggregateClass = "FoodBOMLineItem";

			action.m_request = request;
			this._processFormData(fd);
		} else {
			// No Product selection was made, create blank FoodBOMLineItem
			action = this.getAction();

			request = lang.mixin(new Request(), {
				m_module: MODULE,
				m_operation: NEW_FOOD_BOM_LINE_ITEM,
			});

			action.m_request = request;
			request = this.setNewOperationRequest(action, request, itemProductTypeEnum);
		}
		
		return request;
	},

	/**
	 * @override
	 * 
	 * Add request parameters,
	 * check if actions are called on toolbar or BOMSection
	 */
	_writeRequestParameters(call) {
		this.inherited(arguments);

		// If action called on BOM Section instead of toolbar,
		// Add parameter of BOM Section to request
		const queryResult = this.widget.getQueryResultByDomNode(this.m_element);
		this.bomSectionQueryResult = queryResult ? queryResult : this.bomSectionQueryResult; // if not null update, else keep same value
		if (this.bomSectionQueryResult) {
			const groupParents = this.bomSectionQueryResult.getGroupParents();
			if (groupParents) {
				groupParents.every((result, index) => {
					const groupUrl = result.groupValue;
					const gs = result.level.groupSpec;
					const groupId = gs.heading.m_id;

					if (groupId == "DSSection") {
						call.addParameter(groupId, xml.ATTR_REF, groupUrl);
						return false;
					}

					return true;
				}, this);
			}
		}
	},
});

export = NewFoodBOMLineItemWizard;